import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Dialog,
  Slide,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Button,
  TextField,
  Autocomplete,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  editSpecialCase,
  getSpecialCases,
  setDisplaySpecialCaseModalDetails,
  setSpecialCaseModalData,
} from "../../../components/collabs/collabsSlice";
import { getVarFormattedDate, showErrorToast, showSuccessToast } from "../../../app/utils/helpers";
import Langs from "../../../app/lang/langs";

/* ------------------------------------------------------------------ */
/*  Shared style constants                                             */
/* ------------------------------------------------------------------ */
const TRANSITION_SMOOTH = "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)";
const BORDER_RADIUS_INPUT = "12px";
const BORDER_RADIUS_BUTTON = "14px";

const fieldLabelSx = {
  fontWeight: 600,
  mb: 0.75,
  letterSpacing: "-0.01em",
  color: "text.secondary",
  fontSize: "0.82rem",
  textTransform: "uppercase",
};

const glassmorphicTextFieldSx = {
  mb: 3,
  "& .MuiOutlinedInput-root": {
    borderRadius: BORDER_RADIUS_INPUT,
    backgroundColor: "rgba(255,255,255,0.06)",
    backdropFilter: "blur(6px)",
    transition: TRANSITION_SMOOTH,
    "& fieldset": {
      borderColor: "rgba(0,0,0,0.08)",
      transition: TRANSITION_SMOOTH,
    },
    "&:hover fieldset": {
      borderColor: "rgba(0,0,0,0.18)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "rgba(255,144,21,0.5)",
      boxShadow: "0 0 0 3px rgba(255,144,21,0.12)",
    },
    "&.Mui-focused": {
      backgroundColor: "rgba(255,255,255,0.10)",
    },
  },
  "& input": {
    fontSize: "16px",
    transition: TRANSITION_SMOOTH,
  },
  "& textarea": {
    fontSize: "16px",
  },
  "& input::placeholder, & textarea::placeholder": {
    opacity: 0.45,
  },
};

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function MobileSpecialCaseDetailsDialog() {
  const dispatch = useDispatch();
  const visible = useSelector((state) => state.collabs.displaySpecialCaseModalDetails);
  const specialCaseData = useSelector((state) => state.collabs.specialCaseModalData);
  const allCollabs = useSelector((state) => state.collabs.allCollabsList);
  const specialCasesOptions = useSelector((state) => state.collabs.specialCasesOptions);

  const [tempStartDate, setTempStartDate] = useState(null);
  const [tempEndDate, setTempEndDate] = useState(null);
  const [tempCase, setTempCase] = useState(null);
  const [tempCollab, setTempCollab] = useState(null);
  const [tempComments, setTempComments] = useState(null);
  const [updateBody, setUpdateBody] = useState({});

  const collabOptions = allCollabs.map((collab) => ({ label: collab.label, value: collab.id }));

  // Initialize temp state from specialCaseData when dialog opens
  useEffect(() => {
    if (visible) {
      if (specialCaseData.start_date) {
        setTempStartDate(getVarFormattedDate(specialCaseData.start_date, "yyyy-mm-dd"));
      }
      if (specialCaseData.end_date) {
        setTempEndDate(getVarFormattedDate(specialCaseData.end_date, "yyyy-mm-dd"));
      }
      if (specialCaseData.case) {
        setTempCase(specialCaseData.case);
      }
      if (specialCaseData.id_collab) {
        setTempCollab(specialCaseData.id_collab);
      }
      if (specialCaseData.comments) {
        setTempComments(specialCaseData.comments);
      }
      setUpdateBody({ id: specialCaseData.id });
    }
  }, [specialCaseData, visible]);

  // Track changes for start_date
  useEffect(() => {
    if (specialCaseData?.start_date !== tempStartDate) {
      setUpdateBody((current) => ({ ...current, start_date: tempStartDate }));
    } else {
      setUpdateBody((current) => {
        const newState = { ...current };
        delete newState.start_date;
        return newState;
      });
    }
  }, [tempStartDate]);

  // Track changes for end_date
  useEffect(() => {
    if (specialCaseData?.end_date !== tempEndDate) {
      setUpdateBody((current) => ({ ...current, end_date: tempEndDate }));
    } else {
      setUpdateBody((current) => {
        const newState = { ...current };
        delete newState.end_date;
        return newState;
      });
    }
  }, [tempEndDate]);

  // Track changes for comments
  useEffect(() => {
    if (specialCaseData?.comments !== tempComments) {
      setUpdateBody((current) => ({ ...current, comments: tempComments }));
    } else {
      setUpdateBody((current) => {
        const newState = { ...current };
        delete newState.comments;
        return newState;
      });
    }
  }, [tempComments]);

  // Track changes for case
  useEffect(() => {
    if (specialCaseData?.case != tempCase) {
      setUpdateBody((current) => ({ ...current, case: tempCase }));
    } else {
      setUpdateBody((current) => {
        const newState = { ...current };
        delete newState.case;
        return newState;
      });
    }
  }, [tempCase]);

  // Track changes for id_collab
  useEffect(() => {
    if (specialCaseData?.id_collab !== tempCollab) {
      setUpdateBody((current) => ({ ...current, id_collab: tempCollab }));
    } else {
      setUpdateBody((current) => {
        const newState = { ...current };
        delete newState.id_collab;
        return newState;
      });
    }
  }, [tempCollab]);

  const handleClose = () => {
    dispatch(setSpecialCaseModalData({}));
    dispatch(setDisplaySpecialCaseModalDetails(false));
    setUpdateBody({});
  };

  const handleSave = async () => {
    try {
      if (Object.keys(updateBody).length > 1) {
        const response = await dispatch(
          editSpecialCase({
            special_case: updateBody,
          })
        );
        if (response.payload.responseData.status === 1) {
          setUpdateBody({});
          showSuccessToast("Cas spécial modifié avec succès.");
          dispatch(getSpecialCases());
          handleClose();
        } else {
          showErrorToast(response.payload.error || "Échec de la modification du cas spécial.");
        }
      }
    } catch (error) {
      console.error("Erreur lors de la modification du cas spécial :", error);
      showErrorToast("Erreur interne du serveur.");
    }
  };

  const selectedCollab = collabOptions.find((o) => o.value === tempCollab) || null;
  const selectedCase = specialCasesOptions.find((o) => o.value === tempCase) || null;
  const hasChanges = Object.keys(updateBody).length > 1;

  return (
    <Dialog
      fullScreen
      open={!!visible}
      onClose={handleClose}
      TransitionComponent={Transition}
      sx={{
        zIndex: 1400,
        "& .MuiDialog-paper": {
          background: "var(--surface-ground, #f4f6f8)",
        },
      }}
    >
      <AppBar
        sx={{
          position: "sticky",
          top: 0,
          bgcolor: "rgba(255,255,255,0.82)",
          color: "text.primary",
          boxShadow: "0 1px 12px rgba(0,0,0,0.06)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          borderBottom: "1px solid rgba(0,0,0,0.06)",
        }}
      >
        <Toolbar sx={{ py: 0.5 }}>
          <IconButton
            edge="start"
            onClick={handleClose}
            aria-label="close"
            sx={{
              transition: TRANSITION_SMOOTH,
              borderRadius: "12px",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.06)",
                transform: "scale(1.08)",
              },
              "&:active": { transform: "scale(0.95)" },
            }}
          >
            <CloseIcon />
          </IconButton>
          <Typography
            sx={{
              ml: 1.5,
              flex: 1,
              fontWeight: 600,
              fontSize: "1.15rem",
              letterSpacing: "-0.02em",
            }}
            variant="h6"
          >
            <Langs str="DÉTAILS DU CAS SPÉCIAL" />
          </Typography>
        </Toolbar>
      </AppBar>

      <Box
        sx={{
          p: 2.5,
          pb: 14,
          overflowY: "auto",
          flex: 1,
          "@keyframes fadeSlideIn": {
            from: { opacity: 0, transform: "translateY(10px)" },
            to: { opacity: 1, transform: "translateY(0)" },
          },
          animation: "fadeSlideIn 0.35s cubic-bezier(0, 0, 0.2, 1) both",
        }}
      >
        <Box
          sx={{
            background: "rgba(255,255,255,0.65)",
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
            borderRadius: "16px",
            border: "1px solid rgba(255,255,255,0.45)",
            boxShadow: "0 2px 16px rgba(0,0,0,0.04), 0 0.5px 2px rgba(0,0,0,0.03)",
            p: 2.5,
            transition: TRANSITION_SMOOTH,
          }}
        >
          {/* Collaborateur */}
          <Typography variant="body2" sx={fieldLabelSx}>
            <Langs str="Collaborateur" />
          </Typography>
          <Autocomplete
            options={collabOptions}
            getOptionLabel={(option) => option.label || ""}
            value={selectedCollab}
            onChange={(_, newValue) => setTempCollab(newValue ? newValue.value : null)}
            isOptionEqualToValue={(option, value) => option.value === value?.value}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Sélectionnez un collaborateur"
                sx={glassmorphicTextFieldSx}
              />
            )}
            slotProps={{
              popper: { sx: { zIndex: 1500 } },
            }}
          />

          {/* Cas */}
          <Typography variant="body2" sx={fieldLabelSx}>
            <Langs str="Cas" />
          </Typography>
          <Autocomplete
            options={specialCasesOptions}
            getOptionLabel={(option) => option.label || ""}
            value={selectedCase}
            onChange={(_, newValue) => setTempCase(newValue ? newValue.value : null)}
            isOptionEqualToValue={(option, value) => option.value === value?.value}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Sélectionnez un cas"
                sx={glassmorphicTextFieldSx}
              />
            )}
            slotProps={{
              popper: { sx: { zIndex: 1500 } },
            }}
          />

          {/* Date de debut */}
          <Typography variant="body2" sx={fieldLabelSx}>
            <Langs str="Date de début" />
          </Typography>
          <TextField
            type="date"
            fullWidth
            value={tempStartDate || ""}
            onChange={(e) => setTempStartDate(e.target.value)}
            sx={glassmorphicTextFieldSx}
          />

          {/* Date de fin */}
          <Typography variant="body2" sx={fieldLabelSx}>
            <Langs str="Date de fin" />
          </Typography>
          <TextField
            type="date"
            fullWidth
            value={tempEndDate || ""}
            onChange={(e) => setTempEndDate(e.target.value)}
            inputProps={{
              min: tempStartDate || undefined,
            }}
            sx={glassmorphicTextFieldSx}
          />

          {/* Comments */}
          <Typography variant="body2" sx={fieldLabelSx}>
            <Langs str="Comments" />
          </Typography>
          <TextField
            multiline
            rows={4}
            fullWidth
            value={tempComments || ""}
            onChange={(e) => setTempComments(e.target.value)}
            sx={glassmorphicTextFieldSx}
          />
        </Box>
      </Box>

      {/* Sticky bottom - Save button only visible when there are changes */}
      {hasChanges && (
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            p: 2.5,
            bgcolor: "rgba(255,255,255,0.78)",
            backdropFilter: "blur(18px)",
            WebkitBackdropFilter: "blur(18px)",
            boxShadow: "0 -4px 24px rgba(0,0,0,0.06)",
            borderTop: "1px solid rgba(255,255,255,0.5)",
            zIndex: 1,
            "@keyframes slideUp": {
              from: { opacity: 0, transform: "translateY(20px)" },
              to: { opacity: 1, transform: "translateY(0)" },
            },
            animation: "slideUp 0.3s cubic-bezier(0, 0, 0.2, 1) both",
          }}
        >
          <Button
            variant="contained"
            color="warning"
            fullWidth
            onClick={handleSave}
            sx={{
              minHeight: 52,
              borderRadius: BORDER_RADIUS_BUTTON,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "1.05rem",
              letterSpacing: "-0.01em",
              boxShadow: "0 4px 14px rgba(255,144,21,0.28)",
              transition: TRANSITION_SMOOTH,
              "&:hover": {
                transform: "scale(1.02)",
                boxShadow: "0 6px 20px rgba(255,144,21,0.35)",
              },
              "&:active": {
                transform: "scale(0.98)",
              },
            }}
          >
            <Langs str="SAVE" />
          </Button>
        </Box>
      )}
    </Dialog>
  );
}
